import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PublicLayout } from "../../components/PublicLayout";
import { api, MXN, formatDateMX, fileUrl } from "../../lib/apiClient";
import { useCart } from "../../hooks/useCart";
import { BrandStepMark } from "../../components/BrandOrderMark";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Minus, Plus, Upload, Sparkles, Truck, Store, Loader2, X } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function OrderFlow() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const preId = params.get("producto");
  const quick = params.get("quick") === "1";
  const checkout = params.get("checkout") === "1";
  const { items: cartItems, addItem, removeItem, clearCart, subtotal } = useCart();

  const [step, setStep] = useState(preId ? 2 : checkout && cartItems.length ? 3 : 1);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [cz, setCz] = useState({});          // customization
  const [refImages, setRefImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [idem] = useState(uid());
  const [submitting, setSubmitting] = useState(false);

  // delivery
  const [method, setMethod] = useState("recoger");
  const [dDate, setDDate] = useState("");
  const [dTime, setDTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [dateAvailable, setDateAvailable] = useState(false);
  const [availMsg, setAvailMsg] = useState("");
  const [address, setAddress] = useState({ calle: "", colonia: "", referencias: "", notas: "" });
  const [customer, setCustomer] = useState({ nombre: "", telefono: "", correo: "", comentarios: "" });

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (step === 1) api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, [step]);
  useEffect(() => {
    if (preId) api.get(`/products/${preId}`).then((r) => { setProduct(r.data); initCz(r.data); }).catch(() => {});
  }, [preId]);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    if (checkout && cartItems.length > 0 && !preId) setStep(3);
  }, [checkout, cartItems.length, preId]);

  useEffect(() => {
    if (!dDate) { setSlots([]); setDateAvailable(false); return; }
    api.get("/availability", { params: { date: dDate } }).then((r) => {
      if (r.data.available) { setSlots(r.data.slots); setDateAvailable(true); setAvailMsg(""); }
      else { setSlots([]); setDateAvailable(false); setAvailMsg(r.data.reason || "Fecha no disponible"); setDTime(""); }
    }).catch(() => { setSlots([]); setDateAvailable(false); setAvailMsg("No pudimos consultar esta fecha."); });
  }, [dDate]);

  const initCz = (p) => { setCz({}); setQty(1); };

  const choose = (p) => { setProduct(p); initCz(p); setStep(2); };
  const setC = (k, v) => setCz((c) => ({ ...c, [k]: v }));

  const estimated = product?.requires_quote ? 0 : (Number(product?.base_price || 0) * qty);
  const deliveryFee = method === "envio" ? Number(settings.delivery_cost || 0) : 0;
  const currentLineTotal = product?.requires_quote ? 0 : Number(product?.base_price || 0) * qty;
  const orderSubtotal = subtotal || currentLineTotal;
  const orderTotal = orderSubtotal + deliveryFee;

  const minDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + (product?.min_advance_days || settings.min_advance_days || 2));
    return d.toISOString().slice(0, 10);
  })();

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRefImages((r) => [...r, data.path]);
    } catch (e) { toast.error("No pudimos subir la imagen."); }
    setUploading(false);
  };

  const submit = async () => {
    if (!customer.nombre || !customer.telefono) { toast.error("Necesitamos tu nombre y WhatsApp."); return; }
    if (!dDate || !dateAvailable || !dTime) { toast.error("Elige fecha y horario disponible."); return; }
    const finalCart = cartItems.length ? cartItems : product ? [{
      product_id: product.id,
      product_name: product.name,
      quantity: qty,
      base_price: product.base_price,
      requires_quote: product.requires_quote,
      customization: cz,
    }] : [];
    if (!finalCart.length) { toast.error("Agrega al menos un producto al carrito."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        product_id: finalCart[0].product_id, product_name: finalCart.length > 1 ? "Pedido múltiple" : finalCart[0].product_name, quantity: finalCart.reduce((sum, item) => sum + Number(item.quantity || 1), 0),
        customization: cz, reference_images: refImages,
        delivery_method: method, delivery_date: dDate, delivery_time: dTime,
        address: method === "envio" ? address : {}, event: {},
        customer, estimated_price: orderTotal, idempotency_key: idem,
        cart_items: finalCart.map(({ product_id, quantity, customization }) => ({ product_id, quantity, customization })),
      });
      clearCart();
      navigate("/confirmacion", { state: { order: data } });
    } catch (e) { toast.error(e.response?.data?.detail || "No pudimos crear tu pedido. Intenta de nuevo."); setSubmitting(false); }
  };

  const steps = ["Producto", "Personaliza", "Entrega", "Confirma"];
  const canNext = () => {
    if (step === 1) return !!product || cartItems.length > 0;
    if (step === 2) return !!product;
    if (step === 3) return customer.nombre && customer.telefono && dDate && dateAvailable && dTime;
    return true;
  };
  const addCurrentToCart = () => {
    if (!product) return;
    addItem({
      product_id: product.id,
      product_name: product.name,
      image: product.images?.[0] || "",
      quantity: qty,
      base_price: product.base_price,
      requires_quote: product.requires_quote,
      customization: cz,
    });
    setProduct(null);
    setCz({});
    setQty(1);
    toast.success("Producto agregado al carrito");
    navigate("/carrito");
  };

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((s, i) => {
            const n = i + 1; const active = step >= n;
            return (
              <div key={s} className="flex-1 flex items-center min-w-0">
                <div className="flex flex-col items-center">
                  <BrandStepMark number={n} active={active} label={`Paso ${n}: ${s}`} />
                  <span className={`text-[11px] mt-1 ${active ? "text-vino" : "text-cafe/40"}`}>{s}</span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-1 mx-1 sm:mx-2 rounded-full ${step > n ? "bg-vino/70" : "bg-beige"}`} />}
              </div>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>

            {/* STEP 1: choose product */}
            {step === 1 && (
              <div>
                <h2 className="font-serif text-3xl text-vino mb-1">¿Qué se te antoja?</h2>
                <p className="text-cafe/60 text-sm mb-5">Elige el producto que quieres pedir.</p>
                {cartItems.length > 0 && (
                  <CartSummary items={cartItems} removeItem={removeItem} subtotal={subtotal} title="Tu carrito" />
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {products.map((p) => (
                    <button key={p.id} onClick={() => choose(p)} data-testid={`flow-product-${p.id}`}
                      className="text-left bg-blancoCalido border-2 border-beige rounded-[24px] overflow-hidden hover:border-vino transition-all hover:-translate-y-1">
                      <div className="h-28 bg-beige">{p.images?.[0] && <img src={p.images[0]} className="w-full h-full object-cover" alt="" />}</div>
                      <div className="p-3"><p className="font-serif text-vino leading-tight">{p.name}</p><p className="text-xs text-cafe/50 mt-0.5">{p.requires_quote ? "Cotización" : `Desde ${MXN(p.base_price)}`}</p></div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2: personalize */}
            {step === 2 && product && (
              <div>
                <h2 className="font-serif text-3xl text-vino mb-1">Arma tu pedido</h2>
                <p className="text-cafe/60 text-sm mb-5">Personaliza tu <b>{product.name}</b> como lo imaginas 💛</p>

                {product.requires_quote && (
                  <div className="bg-salvia/15 border border-salvia/40 rounded-2xl p-4 text-sm text-cafe/80 mb-5">
                    Este producto se cotiza según tu evento. Comparte tus detalles y te enviamos el precio por WhatsApp.
                  </div>
                )}

                {/* quantity */}
                <div className="bg-blancoCalido border border-beige rounded-[24px] p-4 sm:p-5 mb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-vino mb-3">Cantidad</p>
                      <Counter value={qty} setValue={setQty} />
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] sm:text-sm text-cafe/50 uppercase tracking-[0.14em]">Subtotal</p>
                      <p className="font-serif text-xl sm:text-3xl text-vino leading-tight">{product.requires_quote ? "Cotización" : MXN(currentLineTotal)}</p>
                      <p className="text-[10px] sm:text-xs text-cafe/40 mt-0.5">{qty} × {product.requires_quote ? "por cotizar" : MXN(product.base_price)}</p>
                    </div>
                  </div>
                </div>

                {quick ? (
                  <div className="bg-salvia/15 border border-salvia/40 rounded-2xl p-4 text-sm text-cafe/80 mb-5">
                    Producto listo para comprar. Sólo elige cantidad y continúa con tus datos de entrega.
                  </div>
                ) : (
                  <>
                    <ChipGroup label="Tamaño / porciones" options={product.sizes} value={cz.tamano} onChange={(v) => setC("tamano", v)} tid="tamano" />
                    <ChipGroup label="Elige tu sabor favorito" options={product.flavors} value={cz.sabor} onChange={(v) => setC("sabor", v)} tid="sabor" flavor />
                    <ChipGroup label="Relleno" options={product.fillings} value={cz.relleno} onChange={(v) => setC("relleno", v)} tid="relleno" />
                    <ChipGroup label="Decoración / cobertura" options={product.decorations} value={cz.decoracion} onChange={(v) => setC("decoracion", v)} tid="decoracion" />
                    <ChipGroupMulti label="Extras / toppings" options={product.extras} value={cz.extras || []} onChange={(v) => setC("extras", v)} tid="extras" />
                    <TextField label="Mensaje / dedicatoria" v={cz.mensaje || ""} onChange={(v) => setC("mensaje", v)} placeholder="Ej. Feliz cumpleaños Ana" tid="mensaje" />
                  </>
                )}

                {/* Reference photos */}
                {!quick && <div className="mb-4">
                  <p className="text-sm font-medium text-vino mb-1.5">Foto de referencia (opcional)</p>
                  <div className="flex flex-wrap gap-3">
                    {refImages.map((path) => (
                      <div key={path} className="relative w-20 h-20 rounded-2xl overflow-hidden border border-beige">
                        <img src={fileUrl(path)} className="w-full h-full object-cover" alt="ref" />
                        <button onClick={() => setRefImages((r) => r.filter((x) => x !== path))} className="absolute top-1 right-1 bg-vino text-crema rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <label data-testid="upload-ref" className="w-20 h-20 rounded-2xl border-2 border-dashed border-rosa flex items-center justify-center cursor-pointer hover:bg-blancoCalido text-vino">
                      {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} />
                    </label>
                  </div>
                </div>}

                {!quick && <TextArea label="Comentarios adicionales" v={cz.comentarios || ""} onChange={(v) => setC("comentarios", v)} tid="cz-comentarios" />}
              </div>
            )}

            {/* STEP 3: delivery + customer */}
            {step === 3 && (
              <div>
                <h2 className="font-serif text-3xl text-vino mb-1">Entrega y tus datos</h2>
                <p className="text-cafe/60 text-sm mb-5">¿Cómo y cuándo lo quieres recibir?</p>
                {cartItems.length > 0 && <CartSummary items={cartItems} removeItem={removeItem} subtotal={subtotal} title="Resumen de carrito" />}

                <p className="font-medium text-vino mb-2">Método de entrega</p>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[["recoger", "Recoger", Store], ["envio", "Envío", Truck]].map(([v, label, Icon]) => (
                    <button key={v} onClick={() => setMethod(v)} data-testid={`metodo-${v}`}
                      className={`rounded-2xl p-4 border-2 flex flex-col items-center gap-2 transition-all ${method === v ? "border-vino bg-vino/5" : "border-beige bg-blancoCalido hover:border-rosa"}`}>
                      <Icon className={`w-6 h-6 ${method === v ? "text-vino" : "text-cafe/50"}`} />
                      <span className={`text-sm ${method === v ? "text-vino font-medium" : "text-cafe/60"}`}>{label}</span>
                    </button>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-2">
                  <div>
                    <label className="text-sm font-medium text-vino mb-1.5 block">Fecha deseada *</label>
                    <input type="date" min={minDate} value={dDate} data-testid="delivery-date" onChange={(e) => setDDate(e.target.value)}
                      className="w-full bg-blancoCalido border-2 border-beige rounded-2xl px-4 py-3 text-cafe focus:outline-none focus:border-vino" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-vino mb-1.5 block">Horario *</label>
                    <select value={dTime} data-testid="delivery-time" onChange={(e) => setDTime(e.target.value)} disabled={!slots.length}
                      className="w-full bg-blancoCalido border-2 border-beige rounded-2xl px-4 py-3 text-cafe focus:outline-none focus:border-vino disabled:opacity-50">
                      <option value="">{!dDate ? "Primero elige fecha" : slots.length ? "Elige un horario" : "Sin horarios disponibles"}</option>
                      {slots.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {!dDate && <p className="text-xs text-cafe/45 mt-1.5">Los horarios se activan después de elegir una fecha disponible.</p>}
                  </div>
                </div>
                {availMsg && <p className="text-sm text-vino bg-rosa/15 border border-rosa/40 rounded-2xl p-3 mb-4" data-testid="avail-msg">{availMsg}</p>}

                {method === "envio" && (
                  <div className="bg-blancoCalido border border-beige rounded-[24px] p-5 mb-4 space-y-3">
                    <p className="font-medium text-vino">Datos de envío {settings.delivery_cost ? `(+${MXN(settings.delivery_cost)})` : ""}</p>
                    <TextField label="Calle y número" v={address.calle} onChange={(v) => setAddress({ ...address, calle: v })} tid="addr-calle" />
                    <TextField label="Colonia" v={address.colonia} onChange={(v) => setAddress({ ...address, colonia: v })} tid="addr-colonia" />
                    <TextField label="Referencias" v={address.referencias} onChange={(v) => setAddress({ ...address, referencias: v })} tid="addr-ref" />
                    <TextField label="Notas para entrega" v={address.notas} onChange={(v) => setAddress({ ...address, notas: v })} tid="addr-notas" />
                  </div>
                )}

                <div className="bg-blancoCalido border border-beige rounded-[24px] p-5 space-y-3">
                  <p className="font-medium text-vino">Tus datos</p>
                  <TextField label="Nombre completo *" v={customer.nombre} onChange={(v) => setCustomer({ ...customer, nombre: v })} tid="cust-nombre" />
                  <TextField label="Teléfono / WhatsApp *" v={customer.telefono} onChange={(v) => setCustomer({ ...customer, telefono: v })} tid="cust-telefono" />
                  <TextField label="Correo (opcional)" v={customer.correo} onChange={(v) => setCustomer({ ...customer, correo: v })} tid="cust-correo" />
                  <TextArea label="Comentarios finales" v={customer.comentarios} onChange={(v) => setCustomer({ ...customer, comentarios: v })} tid="cust-comentarios" />
                </div>
              </div>
            )}

            {/* STEP 4: confirm */}
            {step === 4 && (product || cartItems.length > 0) && (
              <div>
                <h2 className="font-serif text-3xl text-vino mb-1">Tu pedido está casi listo</h2>
                <p className="text-cafe/60 text-sm mb-5">Revisa que todo esté correcto 🌸</p>
                <div className="bg-blancoCalido border border-beige rounded-[28px] p-6 shadow-pastel space-y-3">
                  {(cartItems.length ? cartItems : [{ product_name: product.name, quantity: qty, customization: cz, base_price: product.base_price, requires_quote: product.requires_quote }]).map((item, idx) => (
                    <div key={item.cart_id || idx} className="border-b border-beige last:border-0 pb-3 last:pb-0">
                      <Row label="Producto" value={`${item.product_name} × ${item.quantity}`} />
                      {item.customization?.tamano && <Row label="Tamaño" value={item.customization.tamano} />}
                      {item.customization?.sabor && <Row label="Sabor" value={item.customization.sabor} />}
                      {item.customization?.relleno && <Row label="Relleno" value={item.customization.relleno} />}
                      {item.customization?.decoracion && <Row label="Decoración" value={item.customization.decoracion} />}
                      {item.customization?.extras?.length > 0 && <Row label="Extras" value={item.customization.extras.join(", ")} />}
                      {item.customization?.mensaje && <Row label="Mensaje" value={item.customization.mensaje} />}
                    </div>
                  ))}
                  {refImages.length > 0 && <Row label="Referencias" value={`${refImages.length} imagen(es)`} />}
                  <div className="border-t border-beige my-2" />
                  <Row label="Fecha" value={formatDateMX(dDate)} />
                  <Row label="Hora" value={dTime || "-"} />
                  <Row label="Entrega" value={method === "recoger" ? "Recoger" : "Envío a domicilio"} />
                  <Row label="Cliente" value={customer.nombre} />
                  <Row label="Teléfono" value={customer.telefono} />
                  <div className="border-t border-beige my-2" />
                  <Row label="Subtotal" value={orderSubtotal ? MXN(orderSubtotal) : "Por cotización"} />
                  {deliveryFee > 0 && <Row label="Envío" value={MXN(deliveryFee)} />}
                  <Row label="Total estimado" value={orderTotal ? MXN(orderTotal) : "Por cotización"} bold />
                </div>
                <p className="text-sm text-cafe/70 bg-crema border border-beige rounded-2xl p-4 mt-4">
                  Tu pedido está sujeto a confirmación de disponibilidad. Te contactaremos por WhatsApp para confirmar detalles y pago.
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        <div className="flex gap-3 mt-8">
          {step > (preId ? 2 : checkout ? 3 : 1) && (
            <button data-testid="flow-back" onClick={() => setStep(step === 3 && cartItems.length && !product ? 1 : step - 1)} className="flex-1 border-2 border-vino text-vino rounded-full py-3.5 font-medium hover:bg-vino/5 flex items-center justify-center gap-1">
              <ChevronLeft className="w-5 h-5" /> Atrás
            </button>
          )}
          {step < 4 ? (
            <button data-testid="flow-next" disabled={!canNext()} onClick={() => {
              if (!canNext()) { toast.error("Completa los campos requeridos."); return; }
              if (step === 1) setStep(product ? 2 : 3);
              else if (step === 2) addCurrentToCart();
              else setStep(step + 1);
            }}
              className="flex-1 bg-vino text-crema rounded-full py-3.5 font-medium hover:bg-vinoDark transition-all disabled:opacity-50 flex items-center justify-center gap-1">
              {step === 2 ? "Agregar al carrito" : "Continuar"} <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button data-testid="flow-submit" disabled={submitting} onClick={submit}
              className="flex-1 bg-vino text-crema rounded-full py-3.5 font-medium hover:bg-vinoDark transition-all disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando…</> : <><Sparkles className="w-5 h-5" /> Enviar pedido</>}
            </button>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

const Counter = ({ value, setValue }) => (
  <div className="flex items-center gap-3 sm:gap-4">
    <button onClick={() => setValue(Math.max(1, value - 1))} data-testid="qty-minus" className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-beige text-vino flex items-center justify-center hover:bg-rosa/40"><Minus className="w-4 h-4 sm:w-5 sm:h-5" /></button>
    <span className="text-xl sm:text-2xl font-serif text-vino w-8 sm:w-10 text-center" data-testid="qty-value">{value}</span>
    <button onClick={() => setValue(value + 1)} data-testid="qty-plus" className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-vino text-crema flex items-center justify-center hover:bg-vinoDark"><Plus className="w-4 h-4 sm:w-5 sm:h-5" /></button>
  </div>
);

const CartSummary = ({ items, removeItem, subtotal, title }) => (
  <div className="bg-blancoCalido border border-beige rounded-[26px] p-4 sm:p-5 shadow-pastel mb-6">
    <div className="flex items-center justify-between gap-3 mb-3">
      <div>
        <p className="font-serif text-2xl text-vino">{title}</p>
        <p className="text-xs text-cafe/50">{items.length} producto(s) agregado(s)</p>
      </div>
      <p className="font-serif text-2xl text-vino">{subtotal ? MXN(subtotal) : "Cotización"}</p>
    </div>
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.cart_id} className="flex items-center gap-3 bg-crema border border-beige rounded-2xl p-2.5">
          <div className="w-12 h-12 rounded-xl bg-beige overflow-hidden shrink-0">
            {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-vino truncate">{item.product_name}</p>
            <p className="text-xs text-cafe/50">{item.quantity} × {item.requires_quote ? "cotización" : MXN(item.base_price)}</p>
          </div>
          <button onClick={() => removeItem(item.cart_id)} className="text-vino/70 p-2 hover:bg-beige rounded-full">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  </div>
);

const ChipGroup = ({ label, options, value, onChange, tid, flavor }) => {
  if (!options || !options.length) return null;
  return (
    <div className="mb-6">
      <p className="text-[15px] sm:text-base font-semibold text-vino mb-2.5">{label}</p>
      <div className="flex flex-wrap gap-2.5 sm:gap-3">
        {options.map((o) => {
          const active = value === o;
          return (
            <button key={o} data-testid={`opt-${tid}-${o}`} onClick={() => onChange(active ? "" : o)}
              className={`rounded-full border transition-all min-h-[44px] px-5 sm:px-6 py-2 text-[15px] sm:text-base flex items-center gap-2 shadow-[0_6px_18px_rgba(118,27,56,0.035)] active:scale-[0.98] ${active ? "border-vino/70 bg-blancoCalido text-vino shadow-[0_8px_22px_rgba(118,27,56,0.09)]" : "border-beige bg-blancoCalido/80 text-cafe/70 hover:border-rosa hover:bg-white"}`}>
              {active && <Check className="w-4 h-4 text-salvia shrink-0" />}
              <span>{o}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ChipGroupMulti = ({ label, options, value, onChange, tid }) => {
  if (!options || !options.length) return null;
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="mb-6">
      <p className="text-[15px] sm:text-base font-semibold text-vino mb-2.5">{label}</p>
      <div className="flex flex-wrap gap-2.5 sm:gap-3">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button key={o} data-testid={`opt-${tid}-${o}`} onClick={() => toggle(o)}
              className={`rounded-full border min-h-[44px] px-5 sm:px-6 py-2 text-[15px] sm:text-base transition-all flex items-center gap-2 shadow-[0_6px_18px_rgba(118,27,56,0.035)] active:scale-[0.98] ${active ? "border-vino/70 bg-blancoCalido text-vino shadow-[0_8px_22px_rgba(118,27,56,0.09)]" : "border-beige bg-blancoCalido/80 text-cafe/70 hover:border-rosa hover:bg-white"}`}>
              {active && <Check className="w-4 h-4 text-salvia shrink-0" />} <span>{o}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TextField = ({ label, v, onChange, placeholder = "", tid }) => (
  <div className="mb-5">
    <label className="text-[15px] sm:text-base font-semibold text-vino mb-2 block">{label}</label>
    <input data-testid={tid} value={v} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-blancoCalido/90 border border-beige rounded-[22px] px-5 py-4 text-cafe placeholder-rosa/75 focus:outline-none focus:border-vino/70 focus:bg-white focus:shadow-[0_10px_26px_rgba(118,27,56,0.08)] transition-all" />
  </div>
);

const TextArea = ({ label, v, onChange, tid }) => (
  <div className="mb-5">
    <label className="text-[15px] sm:text-base font-semibold text-vino mb-2 block">{label}</label>
    <textarea data-testid={tid} value={v} rows={3} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-blancoCalido/90 border border-beige rounded-[22px] px-5 py-4 text-cafe placeholder-rosa/75 focus:outline-none focus:border-vino/70 focus:bg-white focus:shadow-[0_10px_26px_rgba(118,27,56,0.08)] transition-all resize-none" />
  </div>
);

const Row = ({ label, value, bold }) => (
  <div className="flex justify-between gap-4 text-sm">
    <span className="text-cafe/60">{label}</span>
    <span className={`text-right ${bold ? "font-semibold text-vino text-base" : "text-cafe"}`}>{value}</span>
  </div>
);
